import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Check,
  Gift,
  Loader2,
  Plus,
  Send,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Button, Skeleton } from "@/components/ui";
import { Chip, MoneyFigure, StatusBanner, Stepper } from "@/components/marketplace";
import { Select } from "@/components/Select";
import { useTranslation } from "@/hooks/useTranslation";
import { formatMoney } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  useCampaignMutations,
  type CampaignObjective,
  type CompensationType,
  type DeliverableFormat,
  type DeliverableSpec,
} from "@/hooks/useMarketplaceCampaigns";
import {
  useActiveBrandProfile,
  useCreatorSearch,
  type CreatorSearchInput,
  type SocialPlatform,
} from "@/hooks/useMarketplace";
import { useInviteCreator } from "@/hooks/useCollaborations";
import { CreatorMatchRow } from "./components/CreatorMatchRow";
import {
  COMPENSATION_TYPES,
  DELIVERABLE_FORMATS,
  OBJECTIVES,
  splitCommission,
} from "./campaignConstants";
import { COUNTRIES, COUNTRY_LABELS, NICHES, PLATFORMS, PLATFORM_LABELS } from "./constants";

const LABEL = "block text-fine font-medium text-neutral-700 mb-1.5";
const FIELD =
  "w-full rounded-control border border-neutral-200 bg-white px-3 py-2.5 text-control text-neutral-900 transition-colors placeholder:text-neutral-400 hover:border-neutral-300";

type StepId = "describe" | "brief" | "creators" | "publish";
const STEP_IDS: StepId[] = ["describe", "brief", "creators", "publish"];

/**
 * Launching a campaign, in four steps.
 *
 * It replaces a single 400-line form whose every field was equally urgent. The
 * order here is the order a brand actually decides in: what they want, what
 * that costs, who does it, and only then publishing.
 *
 * **The fourth step is "Publicar", not "Depositar."** The prototypes end on a
 * deposit screen — "Depositá USD 2.592 y publicá" — and that is the product's
 * whole premise, but the payment clock (F3) is unstarted: nothing funds an
 * `EscrowAccount`, and a button that takes no money while saying it does would
 * be the single most damaging thing on the screen. The step keeps the shape and
 * tells the truth; the wording changes when the deposit exists.
 *
 * Step gating mirrors `validateForPublish` in `marketplace-campaign.service.ts`
 * field for field, so the wizard cannot walk someone to the end and then have
 * the server refuse.
 */
export default function NewCampaign() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createCampaign, publishCampaign, busy } = useCampaignMutations();

  // Shown, not chosen. The server copies this same value onto the campaign, so
  // the preview and the created row cannot disagree. Never defaulted to a
  // literal — an account on a negotiated rate would see someone else's price.
  const { brand, hasNoBrand } = useActiveBrandProfile();
  const commissionPct = brand?.commissionPct ?? 0;

  // Arrives from a creator's profile when the brand had no open campaign to
  // invite them to.
  const [params] = useSearchParams();
  const inviteCreatorId = params.get("creador");
  const { inviteCreator } = useInviteCreator();

  const [step, setStep] = useState(0);
  const [title, setTitle] = useState("");
  const [briefMarkdown, setBriefMarkdown] = useState("");
  const [objective, setObjective] = useState<CampaignObjective>("UGC_CONTENT");
  const [compensationType, setCompensationType] = useState<CompensationType>("CASH");
  const [budgetTotal, setBudgetTotal] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [productDescription, setProductDescription] = useState("");
  const [productValue, setProductValue] = useState("");
  const [countries, setCountries] = useState<string[]>([]);
  const [niches, setNiches] = useState<string[]>([]);
  const [creatorCount, setCreatorCount] = useState("");
  const [deliverables, setDeliverables] = useState<DeliverableSpec[]>([
    { platform: "INSTAGRAM", format: "REEL", quantity: 1 },
  ]);
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const needsProduct = compensationType === "GIFTING" || compensationType === "HYBRID";
  const needsBudget = compensationType !== "GIFTING";

  const budget = Number(budgetTotal) || 0;
  const split = splitCommission(budget, commissionPct);

  const toggle = <T,>(list: T[], set: (v: T[]) => void, value: T) =>
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const updateDeliverable = (index: number, patch: Partial<DeliverableSpec>) =>
    setDeliverables(deliverables.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  // The shortlist is the ordinary creator search, narrowed by the brief. No
  // separate matching endpoint exists and inventing an affinity score client-
  // side would be a number with nothing behind it.
  const searchInput = useMemo<CreatorSearchInput>(() => {
    const next: CreatorSearchInput = {};
    if (countries.length) next.countries = countries;
    if (niches.length) next.niches = niches;
    return next;
  }, [countries, niches]);

  const { creators, loading: searching } = useCreatorSearch(searchInput);
  const matches = creators.filter((c) => c.source === "SELF_REGISTERED");

  // Only known rates count. A creator whose rate nobody has asked for cannot be
  // budgeted against, and treating "unknown" as zero would quietly under-count.
  const selectedRates = matches
    .filter((c) => selected.includes(c.id))
    .reduce((sum, c) => sum + (c.minRate && c.minRate > 0 ? c.minRate : 0), 0);
  const overBudget = needsBudget && budget > 0 && selectedRates > budget;

  /** Exactly the server's rules, so a step can never be "complete" and then rejected. */
  const missingForPublish = (): string[] => {
    const problems: string[] = [];
    if (!title.trim()) problems.push(t("marketplace.campaign.title"));
    if (!briefMarkdown.trim()) problems.push(t("marketplace.campaign.wizard.briefLabel"));
    if (deliverables.length === 0) problems.push(t("marketplace.campaign.deliverables"));
    if (needsProduct && !productDescription.trim())
      problems.push(t("marketplace.campaign.product"));
    if (needsBudget && budget <= 0) problems.push(t("marketplace.campaign.budget"));
    return problems;
  };

  const canLeaveStep = (index: number): boolean => {
    if (index === 0) return title.trim().length > 0 && briefMarkdown.trim().length > 0;
    if (index === 1) {
      if (deliverables.length === 0) return false;
      if (needsProduct && !productDescription.trim()) return false;
      if (needsBudget && budget <= 0) return false;
      return true;
    }
    return true;
  };

  const buildInput = () => ({
    title,
    briefMarkdown,
    objective,
    compensationType,
    currency,
    budgetTotal: needsBudget && budgetTotal ? Number(budgetTotal) : null,
    productDescription: needsProduct && productDescription ? productDescription : null,
    productValue: needsProduct && productValue ? Number(productValue) : null,
    targetCountries: countries,
    targetNiches: niches,
    targetCreatorCount: creatorCount ? Number(creatorCount) : null,
    deliverablesSpec: deliverables,
  });

  /** Create the draft, invite everyone chosen, then publish. */
  const submit = async (publish: boolean) => {
    setError(null);
    try {
      const campaign = await createCampaign(buildInput());

      // Best-effort: the campaign exists either way, and losing it to a failed
      // invitation would be the worse outcome. An invitation may be sent
      // against a DRAFT — `listInvitationsForCreator` filters to OPEN, so it
      // surfaces the moment the campaign is published rather than inviting
      // someone to a brief that does not exist yet.
      const toInvite = [...new Set([...selected, inviteCreatorId].filter(Boolean))] as string[];
      for (const creatorId of toInvite) {
        try {
          await inviteCreator(campaign.id, creatorId);
        } catch {
          /* ignored on purpose — see above */
        }
      }

      if (publish) await publishCampaign(campaign.id);

      navigate(`/campanas/${campaign.id}`, { replace: true });
    } catch (e) {
      const failure = e as { message?: string; missing?: string[] };
      setError(failure.message ?? String(e));
    }
  };

  // Refuse the form rather than the submit. Every field here belongs to a
  // brand, and letting someone fill all of them before the server says "you
  // need a brand profile" wastes the work and explains nothing.
  if (hasNoBrand) {
    return (
      <div className="adv-page">
        <div className="mx-auto max-w-lg rounded-card border border-neutral-200 bg-white px-5 py-10 text-center">
          <h1 className="text-card-title text-neutral-900">
            {t("marketplace.brand.requiredTitle")}
          </h1>
          <p className="mt-2 text-meta text-neutral-500">{t("marketplace.brand.requiredBody")}</p>
          <Button className="mt-5" asChild>
            <Link to="/marca">
              <Building2 />
              {t("marketplace.brand.requiredCta")}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const budgetPanel = (
    <aside className="min-w-0 flex-[1_1_320px] lg:sticky lg:top-[76px]">
      <div className="space-y-4 rounded-card border border-neutral-200 bg-white px-5 py-[18px] shadow-card-rest">
        <p className="adv-label">{t("marketplace.campaign.wizard.budgetPanel")}</p>

        {needsBudget ? (
          <>
            <div className="flex items-end gap-2">
              <span className="adv-mono pb-1 text-meta font-semibold text-neutral-400">
                {currency}
              </span>
              <input
                type="number"
                min="0"
                value={budgetTotal}
                onChange={(e) => setBudgetTotal(e.target.value)}
                placeholder="0"
                className="w-full min-w-0 border-0 bg-transparent p-0 text-figure-xl text-neutral-900 outline-none placeholder:text-neutral-300"
              />
            </div>

            <input
              type="range"
              min={0}
              max={10000}
              step={50}
              value={Math.min(budget, 10000)}
              onChange={(e) => setBudgetTotal(e.target.value)}
              className="w-full accent-steel-600"
              aria-label={t("marketplace.campaign.budget")}
            />

            <Select
              value={currency}
              onChange={setCurrency}
              options={["USD", "UYU", "ARS", "MXN", "COP", "CLP"].map((c) => ({
                value: c,
                label: c,
              }))}
              size="md"
            />

            {budget > 0 && (
              <dl className="space-y-1.5 border-t border-neutral-100 pt-3 text-fine">
                <div className="flex justify-between gap-3">
                  <dt className="text-neutral-500">{t("marketplace.campaign.creatorGets")}</dt>
                  <dd className="adv-mono text-neutral-900">
                    {formatMoney(split.creatorPayout, currency)}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-neutral-500">
                    {t("marketplace.campaign.platformFee")} ({commissionPct}%)
                  </dt>
                  <dd className="adv-mono text-neutral-900">
                    {formatMoney(split.commission, currency)}
                  </dd>
                </div>
                <div className="flex justify-between gap-3 border-t border-neutral-100 pt-2">
                  <dt className="font-semibold text-neutral-700">
                    {t("marketplace.campaign.youPay")}
                  </dt>
                  <dd className="text-card-title-sm text-steel-600">
                    {formatMoney(split.brandTotal, currency)}
                  </dd>
                </div>
              </dl>
            )}

            {/* Commission on top of the rate, not out of it. This is the
                product's promise and the reason the split is shown at all. */}
            <StatusBanner tone="success">
              {t("marketplace.campaign.wizard.guarantee1")}
            </StatusBanner>
          </>
        ) : (
          <p className="text-fine text-neutral-500">{t("marketplace.compensation.GIFTING")}</p>
        )}
      </div>
    </aside>
  );

  const stepBody: Record<StepId, React.ReactNode> = {
    describe: (
      <div className="flex flex-wrap items-start gap-6">
        <div className="min-w-0 flex-[1_1_480px] space-y-5">
          <div>
            <h1 className="text-page-title-sm text-balance text-neutral-900">
              {t("marketplace.campaign.wizard.describeTitle")}
            </h1>
            <p className="mt-2.5 text-lead text-neutral-500">
              {t("marketplace.campaign.wizard.describeLead")}
            </p>
          </div>

          <div>
            <label className={LABEL}>{t("marketplace.campaign.title")}</label>
            <input
              className={FIELD}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("marketplace.campaign.titlePlaceholder")}
            />
          </div>

          {/* Same treatment as the AI bar on /descubrir — the sweep says this
              box takes a sentence, not a keyword. */}
          <div className="group relative overflow-hidden rounded-card border border-neutral-200 bg-white px-5 py-[18px] transition-[border-color,box-shadow] focus-within:border-steel-600/40 focus-within:shadow-ai-hover">
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-0.5 w-[34%] animate-sweep bg-gradient-to-r from-transparent via-steel-500 to-transparent"
            />
            <div className="mb-3 flex items-center gap-1.5">
              <Sparkles className="size-3.5 text-steel-600" strokeWidth={2.2} />
              <span className="text-label uppercase tracking-[0.12em] text-steel-600">
                {t("marketplace.campaign.wizard.briefLabel")}
              </span>
            </div>
            <textarea
              rows={4}
              value={briefMarkdown}
              onChange={(e) => setBriefMarkdown(e.target.value)}
              placeholder={t("marketplace.campaign.wizard.briefPlaceholder")}
              className="min-h-[86px] w-full resize-none bg-transparent text-body leading-relaxed text-neutral-900 outline-none placeholder:text-neutral-400"
            />
          </div>

          <div>
            <label className={LABEL}>{t("marketplace.campaign.wizard.objectiveLabel")}</label>
            <div className="flex flex-wrap gap-2">
              {OBJECTIVES.map((o) => (
                <Chip
                  key={o}
                  variant={objective === o ? "active" : "default"}
                  onClick={() => setObjective(o)}
                >
                  {t(`marketplace.objectives.${o}`)}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <label className={LABEL}>{t("marketplace.campaign.compensation")}</label>
            <div className="flex flex-wrap gap-2">
              {COMPENSATION_TYPES.map((c) => (
                <Chip
                  key={c}
                  variant={compensationType === c ? "active" : "default"}
                  icon={c !== "CASH" ? <Gift className="size-3" strokeWidth={2.4} /> : undefined}
                  onClick={() => setCompensationType(c)}
                >
                  {t(`marketplace.compensation.${c}`)}
                </Chip>
              ))}
            </div>
          </div>
        </div>

        <aside className="min-w-0 flex-[1_1_300px]">
          <StatusBanner tone="info" icon={<Sparkles className="size-[15px]" strokeWidth={2.2} />}>
            {t("marketplace.campaign.assistantHint")}
          </StatusBanner>
          {inviteCreatorId && (
            <StatusBanner
              tone="success"
              className="mt-3"
              icon={<Send className="size-[15px]" strokeWidth={2.2} />}
            >
              {t("marketplace.invite.pendingOnCreate")}
            </StatusBanner>
          )}
        </aside>
      </div>
    ),

    brief: (
      <div className="flex flex-wrap items-start gap-6">
        <div className="min-w-0 flex-[1_1_480px] space-y-6">
          <div>
            <h1 className="text-page-title-sm text-balance text-neutral-900">
              {t("marketplace.campaign.wizard.briefTitle")}
            </h1>
            <p className="mt-2.5 text-lead text-neutral-500">
              {t("marketplace.campaign.wizard.briefLead")}
            </p>
          </div>

          <section>
            <p className={LABEL}>{t("marketplace.campaign.deliverables")}</p>
            <div className="space-y-2">
              {deliverables.map((d, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[1fr_1fr_72px_auto] items-center gap-2 rounded-control border border-neutral-200 bg-neutral-50 p-2"
                >
                  <Select
                    value={d.platform}
                    onChange={(v) => updateDeliverable(i, { platform: v as SocialPlatform })}
                    options={PLATFORMS.map((p) => ({ value: p, label: PLATFORM_LABELS[p] }))}
                    size="md"
                  />
                  <Select
                    value={d.format}
                    onChange={(v) => updateDeliverable(i, { format: v as DeliverableFormat })}
                    options={DELIVERABLE_FORMATS.map((f) => ({
                      value: f,
                      label: t(`marketplace.formats.${f}`),
                    }))}
                    size="md"
                  />
                  <input
                    className={cn(FIELD, "adv-mono text-center")}
                    type="number"
                    min="1"
                    value={d.quantity}
                    onChange={(e) =>
                      updateDeliverable(i, {
                        quantity: Math.max(1, Number(e.target.value) || 1),
                      })
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setDeliverables(deliverables.filter((_, x) => x !== i))}
                    disabled={deliverables.length === 1}
                    className="px-1 text-neutral-400 transition-colors hover:text-danger-fg disabled:opacity-30"
                    aria-label={t("common.delete")}
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              ))}
            </div>
            <Chip
              variant="dashed"
              className="mt-2.5"
              icon={<Plus className="size-3" strokeWidth={2.4} />}
              onClick={() =>
                setDeliverables([
                  ...deliverables,
                  { platform: "INSTAGRAM", format: "STORY", quantity: 1 },
                ])
              }
            >
              {t("marketplace.campaign.addDeliverable")}
            </Chip>
          </section>

          {needsProduct && (
            <section className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <label className={LABEL}>{t("marketplace.campaign.product")}</label>
                <input
                  className={FIELD}
                  value={productDescription}
                  onChange={(e) => setProductDescription(e.target.value)}
                  placeholder={t("marketplace.campaign.productPlaceholder")}
                />
              </div>
              <div>
                <label className={LABEL}>{t("marketplace.campaign.productValue")}</label>
                <input
                  className={FIELD}
                  type="number"
                  min="0"
                  value={productValue}
                  onChange={(e) => setProductValue(e.target.value)}
                  placeholder="0"
                />
              </div>
            </section>
          )}

          <section>
            <p className={LABEL}>{t("marketplace.campaign.targetCountries")}</p>
            <div className="flex flex-wrap gap-2">
              {COUNTRIES.map((c) => (
                <Chip
                  key={c}
                  variant={countries.includes(c) ? "active" : "default"}
                  onClick={() => toggle(countries, setCountries, c)}
                >
                  {COUNTRY_LABELS[c]}
                </Chip>
              ))}
            </div>
            <p className="mt-2 text-micro text-neutral-400">
              {t("marketplace.campaign.targetHint")}
            </p>
          </section>

          <section>
            <p className={LABEL}>{t("marketplace.campaign.targetNiches")}</p>
            <div className="flex flex-wrap gap-2">
              {NICHES.map((n) => (
                <Chip
                  key={n}
                  variant={niches.includes(n) ? "active" : "default"}
                  onClick={() => toggle(niches, setNiches, n)}
                >
                  {t(`marketplace.niches.${n}`, { defaultValue: n })}
                </Chip>
              ))}
            </div>
          </section>

          <section className="sm:w-1/2">
            <label className={LABEL}>{t("marketplace.campaign.creatorCount")}</label>
            <input
              className={FIELD}
              type="number"
              min="1"
              value={creatorCount}
              onChange={(e) => setCreatorCount(e.target.value)}
              placeholder="3"
            />
          </section>
        </div>

        {budgetPanel}
      </div>
    ),

    creators: (
      <div className="flex flex-wrap items-start gap-6">
        <div className="min-w-0 flex-[1_1_480px] space-y-5">
          <div>
            <h1 className="text-page-title-sm text-balance text-neutral-900">
              {t("marketplace.campaign.wizard.creatorsTitle")}
            </h1>
            <p className="mt-2.5 text-lead text-neutral-500">
              {t("marketplace.campaign.wizard.creatorsLead")}
            </p>
          </div>

          {searching && matches.length === 0 ? (
            <div className="space-y-2.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="flex gap-4 rounded-panel border border-neutral-200 bg-white px-3.5 py-3"
                >
                  <Skeleton className="h-[90px] w-18 rounded-panel" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-[52%] rounded-sm" />
                    <Skeleton className="mt-2 h-3 w-[34%] rounded-sm" />
                    <Skeleton className="mt-4 h-3 w-[88%] rounded-sm" />
                    <Skeleton className="mt-2 h-3 w-[61%] rounded-sm" />
                  </div>
                </div>
              ))}
            </div>
          ) : matches.length === 0 ? (
            <StatusBanner tone="warning">
              {t("marketplace.campaign.wizard.creatorsEmpty")}
            </StatusBanner>
          ) : (
            <div className="space-y-2.5">
              {matches.map((c) => (
                <CreatorMatchRow
                  key={c.id}
                  creator={c}
                  selected={selected.includes(c.id)}
                  onToggle={() => toggle(selected, setSelected, c.id)}
                />
              ))}
            </div>
          )}
        </div>

        <aside className="min-w-0 flex-[1_1_320px] lg:sticky lg:top-[76px]">
          <div className="space-y-4 rounded-card border border-neutral-200 bg-white px-5 py-[18px] shadow-card-rest">
            <p className="adv-label">
              {t("marketplace.campaign.wizard.selectedCount", { count: selected.length })}
            </p>

            {needsBudget && budget > 0 && (
              <>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-fine text-neutral-500">
                    {t("marketplace.campaign.wizard.budgetConsumed")}
                  </span>
                  <span
                    className={cn(
                      "adv-mono text-meta font-semibold",
                      overBudget ? "text-warning-fg" : "text-neutral-900",
                    )}
                  >
                    {formatMoney(selectedRates, currency)}
                  </span>
                </div>

                <div className="h-1.5 overflow-hidden rounded-sm bg-neutral-100">
                  <div
                    className={cn(
                      "h-full transition-[width] duration-300",
                      overBudget
                        ? "bg-warning-fg"
                        : "bg-gradient-to-r from-steel-600 to-steel-400",
                    )}
                    style={{
                      width: `${Math.min(100, (selectedRates / budget) * 100)}%`,
                    }}
                  />
                </div>

                {overBudget && (
                  <StatusBanner tone="warning">
                    {t("marketplace.campaign.wizard.budgetOver")}
                  </StatusBanner>
                )}

                <p className="text-micro text-neutral-400">
                  {t("marketplace.campaign.wizard.rateUnknownHint")}
                </p>
              </>
            )}
          </div>
        </aside>
      </div>
    ),

    publish: (
      <div className="mx-auto flex max-w-2xl flex-col items-center text-center">
        <span className="flex size-14 animate-pop-in items-center justify-center rounded-full bg-success-bg text-success-fg">
          <Check className="size-7" strokeWidth={2.6} />
        </span>

        <h1 className="mt-5 text-page-title-sm text-balance text-neutral-900">
          {t("marketplace.campaign.wizard.publishTitle")}
        </h1>
        <p className="mt-2.5 text-lead text-neutral-500">
          {t("marketplace.campaign.wizard.publishLead")}
        </p>

        <div className="mt-7 w-full space-y-4 rounded-card border border-neutral-200 bg-white px-5 py-[18px] text-left shadow-card-rest">
          <div>
            <p className="adv-label">{t("marketplace.campaign.title")}</p>
            <p className="mt-1 text-card-title-sm text-neutral-900">{title || "—"}</p>
          </div>

          <div className="border-t border-neutral-100 pt-3">
            <p className="adv-label">{t("marketplace.campaign.wizard.summaryDeliverables")}</p>
            <ul className="mt-1.5 space-y-1">
              {deliverables.map((d, i) => (
                <li key={i} className="text-meta text-neutral-700">
                  <span className="adv-mono text-steel-600">×{d.quantity}</span>{" "}
                  {t(`marketplace.formats.${d.format}`)} · {PLATFORM_LABELS[d.platform]}
                </li>
              ))}
            </ul>
          </div>

          <div className="border-t border-neutral-100 pt-3">
            <p className="adv-label">{t("marketplace.campaign.wizard.summaryTargeting")}</p>
            <p className="mt-1.5 text-meta text-neutral-700">
              {countries.length || niches.length
                ? [
                    ...countries.map((c) => COUNTRY_LABELS[c]),
                    ...niches.map((n) => t(`marketplace.niches.${n}`, { defaultValue: n })),
                  ].join(" · ")
                : t("marketplace.campaign.wizard.summaryNoTargeting")}
            </p>
          </div>

          {needsBudget && budget > 0 && (
            <div className="flex items-end justify-between gap-3 border-t border-neutral-100 pt-3">
              <MoneyFigure
                label={t("marketplace.campaign.wizard.totalLabel")}
                amount={formatMoney(split.brandTotal, currency)}
                size="lg"
                tone="accent"
              />
            </div>
          )}
        </div>

        <ul className="mt-5 w-full space-y-2 text-left">
          {["guarantee1", "guarantee2", "guarantee3"].map((k) => (
            <li key={k} className="flex items-start gap-2 text-meta text-neutral-600">
              <Check className="mt-0.5 size-4 shrink-0 text-success-fg" strokeWidth={2.6} />
              {t(`marketplace.campaign.wizard.${k}`)}
            </li>
          ))}
        </ul>
      </div>
    ),
  };

  const currentId = STEP_IDS[step];
  const isLast = step === STEP_IDS.length - 1;
  const missing = missingForPublish();

  return (
    <div className="adv-page">
      <Link
        to="/campanas"
        className="mb-6 inline-flex items-center gap-1.5 text-meta text-neutral-500 transition-colors hover:text-neutral-900"
      >
        <ArrowLeft className="size-4" />
        {t("marketplace.campaign.backToList")}
      </Link>

      <Stepper
        className="mb-9 overflow-x-auto"
        current={step}
        onStep={setStep}
        steps={STEP_IDS.map((id, i) => ({
          id,
          label: t(`marketplace.campaign.wizard.step${i + 1}`),
        }))}
      />

      {error && (
        <StatusBanner tone="danger" className="mb-5">
          {error}
        </StatusBanner>
      )}

      {stepBody[currentId]}

      <div className="mt-9 flex flex-wrap items-center justify-between gap-3 border-t border-neutral-100 pt-5">
        <Button
          variant="ghost"
          disabled={step === 0}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
        >
          <ArrowLeft />
          {t("marketplace.campaign.wizard.back")}
        </Button>

        <div className="flex items-center gap-2">
          {isLast && (
            <Button variant="secondary" disabled={busy} onClick={() => submit(false)}>
              {t("marketplace.campaign.wizard.saveDraft")}
            </Button>
          )}

          {isLast ? (
            <Button disabled={busy || missing.length > 0} onClick={() => submit(true)}>
              {busy ? <Loader2 className="animate-spin" /> : <Send />}
              {busy
                ? t("marketplace.campaign.wizard.publishing")
                : selected.length > 0
                  ? t("marketplace.campaign.wizard.inviteCount", { count: selected.length })
                  : t("marketplace.campaign.wizard.publishOnly")}
            </Button>
          ) : (
            <Button
              disabled={!canLeaveStep(step)}
              onClick={() => setStep((s) => Math.min(STEP_IDS.length - 1, s + 1))}
            >
              {t("marketplace.campaign.wizard.next")}
            </Button>
          )}
        </div>
      </div>

      {isLast && missing.length > 0 && (
        <p className="mt-3 text-right text-fine text-warning-fg">
          {t("marketplace.campaign.wizard.missing", { fields: missing.join(", ") })}
        </p>
      )}
    </div>
  );
}
